import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToId } from "./Nav";

const EASE = [0.22, 1, 0.36, 1];
const HEADLINE_LINES = ["Turn more patient", "enquiries into booked", "consultations."];

const HeroArt = () => (
  <svg
    aria-hidden="true"
    className="pointer-events-none absolute right-[-6%] top-1/2 hidden w-[54%] -translate-y-1/2 text-teal md:block"
    viewBox="0 0 640 560"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    opacity="0.16"
  >
    <rect x="40" y="60" width="300" height="180" rx="18" />
    <rect x="70" y="95" width="150" height="12" rx="6" />
    <rect x="70" y="125" width="220" height="12" rx="6" />
    <rect x="70" y="155" width="180" height="12" rx="6" />
    <path d="M340 150 H460 Q500 150 500 190 V250" strokeDasharray="6 8" />
    <rect x="400" y="250" width="220" height="200" rx="18" />
    <circle cx="445" cy="305" r="16" />
    <path d="M438 305 l5 6 l10 -12" />
    <rect x="475" y="295" width="110" height="12" rx="6" />
    <rect x="430" y="350" width="160" height="12" rx="6" />
    <rect x="430" y="380" width="120" height="12" rx="6" />
    <path d="M190 240 V330 Q190 370 230 370 H400" strokeDasharray="6 8" />
    <circle cx="120" cy="480" r="34" />
    <path d="M104 480 l11 12 l21 -24" />
    <path d="M154 480 H400" strokeDasharray="6 8" />
    <rect x="480" y="70" width="120" height="96" rx="14" />
    <path d="M520 166 L514 190 L540 166" />
    <rect x="500" y="96" width="80" height="10" rx="5" />
    <rect x="500" y="120" width="60" height="10" rx="5" />
  </svg>
);

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 22 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: EASE },
});

export default function Hero() {
  const { scrollY } = useScroll();
  const artY = useTransform(scrollY, [0, 800], [0, 120]);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative overflow-hidden pb-24 pt-36 md:pb-36 md:pt-48"
    >
      <motion.div style={{ y: artY }} className="absolute inset-0" aria-hidden="true">
        <HeroArt />
      </motion.div>
      <div className="relative mx-auto max-w-6xl px-6">
        <div className="max-w-2xl">
          <motion.p
            data-testid="hero-eyebrow"
            className="text-xs font-semibold uppercase tracking-[0.22em] text-teal"
            {...fadeUp(0.1)}
          >
            For private &amp; cosmetic dental practices
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="mt-6 font-display text-4xl font-medium leading-[1.12] tracking-tight text-ink sm:text-5xl lg:text-6xl"
          >
            {HEADLINE_LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-[0.06em]">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1.05, delay: 0.3 + i * 0.15, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            className="mt-7 text-base leading-relaxed text-ink/70 md:text-lg"
            {...fadeUp(0.85)}
          >
            You already spend money getting people to enquire. Leadtochair makes
            sure fewer of those enquiries go quiet — by responding faster,
            following up properly, and re-engaging people who haven&apos;t
            booked yet.
          </motion.p>

          <motion.div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center" {...fadeUp(1)}>
            <motion.button
              data-testid="hero-book-demo-btn"
              onClick={() => scrollToId("contact")}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              className="rounded-full bg-teal px-8 py-3.5 text-sm font-medium tracking-wide text-white transition-colors hover:bg-teal-dark"
            >
              Book a Demo
            </motion.button>
            <motion.button
              data-testid="hero-see-how-it-works-btn"
              onClick={() => scrollToId("demo")}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              className="rounded-full border border-ink/25 px-8 py-3.5 text-sm font-medium tracking-wide text-ink transition-colors hover:border-teal hover:text-teal"
            >
              See How It Works
            </motion.button>
          </motion.div>

          <motion.p
            data-testid="hero-trust-line"
            className="mt-10 max-w-xl border-l-2 border-teal/40 pl-5 text-sm leading-relaxed text-ink/60"
            {...fadeUp(1.15)}
          >
            Built specifically around how private dental practices actually
            receive enquiries — through the phone, the website, WhatsApp and
            email — not a generic sales tool adapted for dentistry.
          </motion.p>
        </div>
      </div>
    </section>
  );
}
